import { ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";
import Container from "../global/container";
import Icons from "../global/icons";
import { Button } from "../ui/button";
import { OrbitingCircles } from "../ui/orbiting-circles";

const Hero = () => {
  return (
    <div
      className="relative flex flex-col items-center justify-center w-full py-24 lg:py-32 min-h-[85vh] lg:min-h-screen overflow-hidden"
      data-oid="vprvfwh"
    >
      <div
        className="flex flex-col items-center justify-center gap-y-10 relative w-full"
        data-oid="wa1h3sk"
      >
        <Container
          className="hidden lg:flex absolute inset-0 top-0 mb-auto flex-col items-center justify-center w-full min-h-screen -z-10"
          data-oid="3y89fnz"
        >
          <OrbitingCircles speed={0.5} radius={300} data-oid="-l790he">
            <Icons.circle1
              className="size-4 text-foreground/70"
              data-oid="mmwwbnh"
            />
            <Icons.circle2
              className="size-1 text-foreground/80"
              data-oid="cdtb4jg"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.25} radius={400} data-oid="e9qz-pm">
            <Icons.circle2
              className="size-1 text-foreground/50"
              data-oid=":4-t8zn"
            />
            <Icons.circle1
              className="size-4 text-foreground/60"
              data-oid="7zxxl8d"
            />
            <Icons.circle2
              className="size-1 text-foreground/90"
              data-oid="l8eq948"
            />
          </OrbitingCircles>
          <OrbitingCircles speed={0.1} radius={500} data-oid="wukck9z">
            <Icons.circle2
              className="size-1 text-foreground/50"
              data-oid="sgbbgha"
            />
            <Icons.circle2
              className="size-1 text-foreground/90"
              data-oid="hz1h.kr"
            />
            <Icons.circle1
              className="size-4 text-foreground/60"
              data-oid="58tg-dw"
            />
            <Icons.circle2
              className="size-1 text-foreground/90"
              data-oid="4u:n_i."
            />
          </OrbitingCircles>
        </Container>

        <div
          className="flex flex-col items-center justify-center text-center gap-y-4 bg-background/0 w-full relative z-10"
          data-oid="esmgoyu"
        >
          <Container
            className="relative hidden lg:block overflow-hidden"
            data-oid=".p4w2aw"
          >
            <button
              className="group relative grid overflow-hidden rounded-full px-2 py-1 shadow-[0_1000px_0_0_hsl(0_0%_15%)_inset] transition-colors duration-200 mx-auto backdrop-blur-sm"
              data-oid="u18e9jy"
            >
              <span data-oid="5:lp_mo">
                <span
                  className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,white_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]"
                  data-oid="v2hby8z"
                />
              </span>
              <span
                className="backdrop absolute inset-[1px] rounded-full bg-background/90 transition-colors duration-200 group-hover:bg-neutral-800/90"
                data-oid="wbxo2ok"
              />
              <span
                className="z-10 py-0.5 text-sm text-neutral-100 flex items-center"
                data-oid="j46kg4x"
              >
                <span
                  className="px-2 py-[0.5px] h-[18px] tracking-wide flex items-center justify-center rounded-full bg-gradient-to-r from-sky-400 to-blue-600 text-[9px] font-medium mr-2 text-white"
                  data-oid="jzol42-"
                >
                  NEW
                </span>
                From our headquarters
              </span>
            </button>
          </Container>
          <Container delay={0.15} data-oid="s.su-o0">
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-center !leading-tight max-w-4xl mx-auto"
              data-oid="toyp4hx"
            >
              Transform your{" "}
              <span className="" data-oid="bz21soa">
                marketing{" "}
              </span>
              with AI Precision
            </h1>
          </Container>
          <Container delay={0.2} data-oid="p73bwq.">
            <p
              className="max-w-xl mx-auto mt-2 text-base lg:text-lg text-center text-muted-foreground"
              data-oid="k:6so5m"
            >
              AI-powered automation and insights to maximize your campaigns and
              grow your brand.
            </p>
          </Container>
          <Container delay={0.25} className="z-20" data-oid="fvrvvuk">
            <div
              className="flex items-center justify-center mt-6 gap-x-4"
              data-oid="3c-vdeo"
            >
              <Link
                to="/demo"
                className="flex items-center gap-2 group"
                data-oid="-tbtaw0"
              >
                <Button size="lg" data-oid=":wo.wks">
                  Start Free Trial
                  <ArrowRightIcon
                    className="size-4 group-hover:translate-x-1 transition-all duration-300"
                    data-oid="njl1ykb"
                  />
                </Button>
              </Link>
            </div>
          </Container>
          <Container delay={0.3} className="relative w-full" data-oid="8uyfqs6">
            <div
              className="relative rounded-xl lg:rounded-[32px] mt-8 sm:mt-14 lg:mt-20 max-w-6xl mx-auto"
              data-oid="ggtuf4d"
            >
              <div
                className="relative h-[360px] sm:h-[440px] lg:h-[620px]"
                data-oid="19qeebe"
              >
                <div
                  className="absolute left-0 top-0 w-[82%] sm:w-[75%] lg:w-[78%] h-[70%] sm:h-[72%] lg:h-auto rounded-[24px] shadow-2xl overflow-hidden lg:aspect-[3/2]"
                  data-oid="primary-image"
                >
                  <img
                    src="/images/datacenter.jpg"
                    alt="Marketing analytics dashboard"
                    className="size-full object-cover"
                    data-oid="primary-img"
                  />
                </div>
                <div
                  className="absolute right-0 bottom-0 w-[70%] sm:w-[60%] lg:w-[58%] h-[65%] sm:h-[65%] lg:h-auto rounded-[20px] shadow-2xl translate-y-6 sm:translate-y-8 translate-x-2 sm:translate-x-6 overflow-hidden lg:aspect-[3/2]"
                  data-oid="secondary-image"
                >
                  <img
                    src="/images/realistic.jpg"
                    alt="Team collaborating around marketing data"
                    className="size-full object-cover"
                    data-oid="secondary-img"
                  />
                </div>
              </div>
            </div>
            <div
              className="pointer-events-none bg-gradient-to-t from-background via-background/85 to-transparent absolute inset-x-0 -bottom-6 sm:-bottom-10 lg:-bottom-12 w-full h-[60%]"
              data-oid="u0ql__t"
            ></div>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default Hero;
